/**
 * "Auto-update LinkedIn network" — whether the hourly cron re-reads a user's LinkedIn
 * connections, and how often. One answer for every surface that shows or acts on it.
 *
 * It is stored in two columns of `user`: the flag `connectionScraping` and the interval
 * `connectionScrapingInterval`. The cron selects a user only when the flag is on AND an interval
 * is stored (`connectionAutoUpdateOnWhere` in apps/api/src/users/repositories/), so a flag
 * without an interval is Off. PREC-4540: the Account card read the flag alone and filled the
 * missing interval with "Weekly", so 56 of 80 customer users with a connected LinkedIn were shown
 * "On · Weekly" while the cron never ran for them.
 *
 * Read the state through `connectionAutoUpdateOf` — never off `connectionScraping` alone (gate
 * rule `connection-auto-update-single-source`). The API keeps the two columns together on every
 * write (`withConsistentConnectionAutoUpdate`), and a new user starts Off.
 */

export const CONNECTION_AUTO_UPDATE_INTERVALS = ["DAILY", "WEEKLY", "MONTHLY"] as const;

export type ConnectionAutoUpdateInterval = (typeof CONNECTION_AUTO_UPDATE_INTERVALS)[number];

/** The interval stored when a user switches auto-update on without choosing one. */
export const DEFAULT_CONNECTION_AUTO_UPDATE_INTERVAL: ConnectionAutoUpdateInterval = "WEEKLY";

/** The two stored columns, as any user-shaped object carries them. */
export interface ConnectionAutoUpdateSettings {
	connectionScraping?: boolean | null;
	connectionScrapingInterval?: string | null;
}

/** The effective setting: `interval` is set exactly when `enabled` is true. */
export interface ConnectionAutoUpdate {
	enabled: boolean;
	interval: ConnectionAutoUpdateInterval | null;
}

const isInterval = (value: unknown): value is ConnectionAutoUpdateInterval =>
	(CONNECTION_AUTO_UPDATE_INTERVALS as readonly unknown[]).includes(value);

/**
 * The auto-update setting the scheduler acts on. On only when the flag is on and a known interval
 * is stored; anything else, including a missing user, is Off.
 */
export function connectionAutoUpdateOf(
	settings: ConnectionAutoUpdateSettings | null | undefined
): ConnectionAutoUpdate {
	const interval = settings?.connectionScrapingInterval;

	if (settings?.connectionScraping !== true || !isInterval(interval)) {
		return { enabled: false, interval: null };
	}

	return { enabled: true, interval };
}
